/**
 * Thin FE wrappers for `/api/v1/auth/*`.
 *
 * Each function calls `apiFetch` and hands the raw response through the
 * matching zod schema from `@dragonis-cdr/shared`. Validation here is not
 * paranoid defence against the server — it is defence against shape drift
 * between FE and API, and it produces a much better error at the edge than a
 * deep `undefined` later in a component.
 *
 * There is deliberately no `login()` wrapper. Under Entra SSO sign-in is a
 * full-page browser navigation to `/api/v1/auth/login` whose response is a 302
 * to Entra the browser must follow across origin — routing it through
 * `apiFetch` would either break CORS or strip the redirect. The sign-in
 * affordance lives in `LoginPage.tsx` as a plain navigation, not here.
 */
import { auth } from '@dragonis-cdr/shared';

import { apiFetch } from './client';

export type MeResponse = auth.MeResponse;

/**
 * POST /api/v1/auth/logout — 204 No Content on success. The server clears
 * the cookie; the caller is responsible for clearing in-memory session state.
 */
export async function logout(): Promise<void> {
  await apiFetch<void>('/auth/logout', { method: 'POST' });
}

/**
 * GET /api/v1/auth/me — returns the current session summary. Callers MUST
 * treat a 401 ApiError as "anonymous" rather than as a crash.
 */
export async function me(): Promise<MeResponse> {
  const body = await apiFetch<unknown>('/auth/me');
  return auth.MeResponse.parse(body);
}
